import * as React from "react";
import {
  Box,
  Text,
  SimpleGrid,
  Input,
  HStack,
  Center,
  FormControl,
  Select,
  Switch,
  Checkbox,
  FormLabel,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

const Recurrence = ({ field, rules, form, ...props }) => {
  // Simple function to get start date from form values
  const getStartDate = () => {
    const startDate = form.values.ee_start_at || form.values.ae_start_at || form.values.start_at;
    console.log('getStartDate returning:', startDate, 'from values:', {
      ee_start_at: form.values.ee_start_at,
      ae_start_at: form.values.ae_start_at,
      start_at: form.values.start_at
    });
    return startDate;
  };
  
  const [months, setMonths] = useState(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']);
  const [repeatPeriods, setRepeatPeriods] = useState(['Yearly', 'Monthly', 'Weekly', 'Daily']);
  const [occurrencePeriods, setOccurrencePeriods] = useState(['First', 'Second', 'Third', 'Fourth', 'Last']);
  const [days, setDays] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Day', 'Weekday', 'Weekend Day']);
  const [selectedRepeatPeriod, setSelectedRepeatPeriod] = useState('');
  const [yearlyFirstSwitch, setYearlyFirstSwitch] = useState(false);
  const [yearlySecondSwitch, setYearlySecondSwitch] = useState(false);
  const [yearlyDays, setYearlyDays] = useState([]);
  const [monthlyDays, setMonthlyDays] = useState([]);
  const [monthlyFirstSwitch, setMonthlyFirstSwitch] = useState(false);
  const [monthlySecondSwitch, setMonthlySecondSwitch] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [endType, setEndType] = useState('By'); // 'By' or 'After'
  const [numberOfOccurrences, setNumberOfOccurrences] = useState(1);

  const onRepeatChange = (e) => {
    console.log('onRepeatChange called with:', e.target.value);
    form.setFieldValue('recurrence', e.target.value);
    setSelectedRepeatPeriod(e.target.value);
    
    // Auto-select the day of the week when weekly recurrence is chosen and start date is set
    if (e.target.value === 'weekly') {
      // Set default weekly weeks to 1
      form.setFieldValue('r_weekly_weeks', 1);
      
      const startDate = getStartDate();
      console.log('Weekly selected, startDate:', startDate);
      if (startDate) {
        const date = new Date(startDate);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        console.log('Extracted dayName:', dayName);
        
        // Set the selected day automatically
        const newSelectedDays = [dayName];
        console.log('Setting newSelectedDays:', newSelectedDays);
        setSelectedDays(newSelectedDays);
        form.setFieldValue('r_weekly_day', newSelectedDays);
      }
    }
    
    // Set defaults for other recurrence types
    if (e.target.value === 'daily') {
      form.setFieldValue('r_daily_days', 1);
    }
    
    if (e.target.value === 'monthly') {
      form.setFieldValue('r_monthly_months', 1);
    }
  }

  const onYearlyFirstSwitchChange = () => {
    form.setFieldValue('r_yearly_first', yearlyFirstSwitch);
    setYearlyFirstSwitch(!yearlyFirstSwitch);
    if (yearlyFirstSwitch != yearlySecondSwitch)
      setYearlySecondSwitch(!yearlySecondSwitch);
  }

  const onYearlySecondSwitchChange = () => {
    form.setFieldValue('r_yearly_second', yearlySecondSwitch);
    setYearlySecondSwitch(!yearlySecondSwitch);
    if (yearlyFirstSwitch != yearlySecondSwitch)
      setYearlyFirstSwitch(!yearlyFirstSwitch);
  }

  const onYearlyMonthChange = (e) => {
    form.setFieldValue('r_yearly_month', e.target.value);
    setYearlyDays(getAllDaysInMonth(months.indexOf(e.target.value), 2023));
  }

  const onYearlyDayChange = (e) => {
    form.setFieldValue('r_yearly_day', e.target.value);
  }

  const onYearlyWeekdayChange = (e) => {
    form.setFieldValue('r_yearly_weekday', e.target.value);
  }

  const onYearlySecondMonthChange = (e) => {
    form.setFieldValue('r_yearly_second_month', e.target.value);
  }

  const onMonthlyMonthsChange = (e) => {
    form.setFieldValue('r_monthly_months', e.target.value);
  }

  const onMonthlyFirstDayChange = (e) => {
    form.setFieldValue('r_monthly_day', e.target.value);
  }

  const onMonthlyOccurrenceChange = (e) => {
    form.setFieldValue('r_monthly_occurrence', e.target.value);
  }

  const onMonthlyWeekdayChange = (e) => {
    form.setFieldValue('r_monthly_weekday', e.target.value);
  }

  const onWeeklyWeeksChange = (e) => {
    form.setFieldValue('r_weekly_weeks', e.target.value);
  }

  const onWeeklyDayChange = (e) => {
    const currentValue = form.values.r_weekly_day || [];
    const dayValue = e.target.value;
    let newSelectedDays;
    
    if (Array.isArray(currentValue)) {
      if (currentValue.includes(dayValue)) {
        newSelectedDays = currentValue.filter(day => day !== dayValue);
      } else {
        newSelectedDays = [...currentValue, dayValue];
      }
    } else {
      newSelectedDays = [dayValue];
    }
    
    setSelectedDays(newSelectedDays);
    form.setFieldValue('r_weekly_day', newSelectedDays);
  }

  const onDailyDaysChange = (e) => {
    form.setFieldValue('r_daily_days', e.target.value);
  }

  const onRecurrenceEndChange = (e) => {
    form.setFieldValue('r_end_date', e.target.value);
  }

  const onEndTypeChange = (e) => {
    setEndType(e.target.value);
    form.setFieldValue('r_end_type', e.target.value);
  }

  const onNumberOfOccurrencesChange = (e) => {
    const occurrences = parseInt(e.target.value) || 1;
    setNumberOfOccurrences(occurrences);
    form.setFieldValue('r_number_of_occurrences', occurrences);
    
    // Calculate and update the recurrence end date based on number of occurrences
    updateRecurrenceEndDate(occurrences);
  }

  const updateRecurrenceEndDate = (occurrences) => {
    const startDate = getStartDate();
    if (!startDate || !selectedRepeatPeriod) {
      return;
    }
    
    const start = new Date(startDate);
    let endDate = new Date(start);
    
    switch (selectedRepeatPeriod) {
      case 'daily':
        const dailyDays = parseInt(form.values.r_daily_days) || 1;
        endDate.setDate(start.getDate() + (occurrences - 1) * dailyDays);
        break;
      case 'weekly':
        const weeklyDays = form.values.r_weekly_day || [];
        const weeklyWeeks = parseInt(form.values.r_weekly_weeks) || 1;
        if (weeklyDays.length > 0) {
          // Calculate based on selected days of the week
          let currentDate = new Date(start);
          let occurrenceCount = 0;
          while (occurrenceCount < occurrences) {
            const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
            if (weeklyDays.includes(dayName)) {
              occurrenceCount++;
            }
            if (occurrenceCount < occurrences) {
              currentDate.setDate(currentDate.getDate() + 1);
            }
          }
          endDate = currentDate;
        } else {
          endDate.setDate(start.getDate() + (occurrences - 1) * 7 * weeklyWeeks);
        }
        break;
      case 'monthly':
        const monthlyMonths = parseInt(form.values.r_monthly_months) || 1;
        endDate.setMonth(start.getMonth() + (occurrences - 1) * monthlyMonths);
        break;
      case 'yearly':
        endDate.setFullYear(start.getFullYear() + (occurrences - 1));
        break;
      default:
        return;
    }
    
    // Format the date for the datetime-local input
    const formattedDate = endDate.toISOString().slice(0, 16);
    form.setFieldValue('r_end_date', formattedDate);
  }

  const generateRecurrenceSummary = () => {
    if (!selectedRepeatPeriod) {
      return "No recurrence pattern selected.";
    }

    let summary = "";

    switch (selectedRepeatPeriod) {
      case 'daily':
        const dailyDays = parseInt(form.values.r_daily_days) || 1;
        summary = `This event repeats every ${dailyDays} day${dailyDays > 1 ? 's' : ''}.`;
        break;
      
      case 'weekly':
        const weeklyDays = form.values.r_weekly_day || [];
        const weeklyWeeks = parseInt(form.values.r_weekly_weeks) || 1;
        
        if (weeklyDays.length > 0) {
          const dayNames = weeklyDays.map(day => {
            const dayMap = {
              'Mon': 'Monday',
              'Tue': 'Tuesday', 
              'Wed': 'Wednesday',
              'Thu': 'Thursday',
              'Fri': 'Friday',
              'Sat': 'Saturday',
              'Sun': 'Sunday'
            };
            return dayMap[day] || day;
          });
          
          if (dayNames.length === 1) {
            summary = `This event repeats every ${weeklyWeeks} week${weeklyWeeks > 1 ? 's' : ''} on ${dayNames[0]}.`;
          } else {
            const lastDay = dayNames.pop();
            summary = `This event repeats every ${weeklyWeeks} week${weeklyWeeks > 1 ? 's' : ''} on ${dayNames.join(', ')} and ${lastDay}.`;
          }
        } else {
          summary = `This event repeats every ${weeklyWeeks} week${weeklyWeeks > 1 ? 's' : ''}.`;
        }
        break;
      
      case 'monthly':
        const monthlyMonths = parseInt(form.values.r_monthly_months) || 1;
        const monthlyDay = parseInt(form.values.r_monthly_day) || 1;
        const monthlyOccurrence = form.values.r_monthly_occurrence;
        const monthlyDaytext = form.values.r_monthly_daytext;
        
        if (monthlyOccurrence && monthlyDaytext) {
          summary = `This event repeats every ${monthlyMonths} month${monthlyMonths > 1 ? 's' : ''} on the ${monthlyOccurrence.toLowerCase()} ${monthlyDaytext.toLowerCase()}.`;
        } else {
          summary = `This event repeats every ${monthlyMonths} month${monthlyMonths > 1 ? 's' : ''} on day ${monthlyDay}.`;
        }
        break;
      
      case 'yearly':
        const yearlyMonth = form.values.r_yearly_month;
        const yearlyDay = parseInt(form.values.r_yearly_day) || 1;
        const yearlyOccurrence = form.values.r_yearly_occurrence;
        const yearlyDaytext = form.values.r_yearly_daytext;
        
        if (yearlyOccurrence && yearlyDaytext) {
          summary = `This event repeats every year on the ${yearlyOccurrence.toLowerCase()} ${yearlyDaytext.toLowerCase()} of ${yearlyMonth}.`;
        } else if (yearlyMonth) {
          summary = `This event repeats every year on ${yearlyMonth} ${yearlyDay}.`;
        } else {
          summary = "This event repeats every year.";
        }
        break;
      
      default:
        summary = "Recurrence pattern not recognized.";
    }

    // Add end condition
    if (endType === 'After' && numberOfOccurrences > 0) {
      summary += ` The event will occur ${numberOfOccurrences} time${numberOfOccurrences > 1 ? 's' : ''}.`;
      
      // Calculate and show the calculated end date
      const startDate = getStartDate();
      if (startDate && selectedRepeatPeriod) {
        const start = new Date(startDate);
        let calculatedEndDate = new Date(start);
        
        switch (selectedRepeatPeriod) {
          case 'daily':
            const dailyDays = parseInt(form.values.r_daily_days) || 1;
            calculatedEndDate.setDate(start.getDate() + (numberOfOccurrences - 1) * dailyDays);
            break;
          case 'weekly':
            const weeklyDays = form.values.r_weekly_day || [];
            const weeklyWeeks = parseInt(form.values.r_weekly_weeks) || 1;
            if (weeklyDays.length > 0) {
              let currentDate = new Date(start);
              let occurrenceCount = 0;
              while (occurrenceCount < numberOfOccurrences) {
                const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
                if (weeklyDays.includes(dayName)) {
                  occurrenceCount++;
                }
                if (occurrenceCount < numberOfOccurrences) {
                  currentDate.setDate(currentDate.getDate() + 1);
                }
              }
              calculatedEndDate = currentDate;
            } else {
              calculatedEndDate.setDate(start.getDate() + (numberOfOccurrences - 1) * 7 * weeklyWeeks);
            }
            break;
          case 'monthly':
            const monthlyMonths = parseInt(form.values.r_monthly_months) || 1;
            calculatedEndDate.setMonth(start.getMonth() + (numberOfOccurrences - 1) * monthlyMonths);
            break;
          case 'yearly':
            calculatedEndDate.setFullYear(start.getFullYear() + (numberOfOccurrences - 1));
            break;
        }
        
        const formattedCalculatedEndDate = calculatedEndDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        summary += ` The last occurrence will be on ${formattedCalculatedEndDate}.`;
      }
    } else if (form.values.r_end_date) {
      const endDate = new Date(form.values.r_end_date);
      const formattedEndDate = endDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      summary += ` The event will end on ${formattedEndDate}.`;
    }

    return summary;
  }

  const onMonthlyFirstSwitchChange = () => {
    form.setFieldValue('r_monthly_first', monthlyFirstSwitch);
    setMonthlyFirstSwitch(!monthlyFirstSwitch);
    if (monthlyFirstSwitch != monthlySecondSwitch)
      setMonthlySecondSwitch(!monthlySecondSwitch);
  }

  const onMonthlySecondSwitchChange = () => {
    form.setFieldValue('r_monthly_second', monthlySecondSwitch);
    setMonthlySecondSwitch(!monthlySecondSwitch);
    if (monthlyFirstSwitch != monthlySecondSwitch)
      setMonthlyFirstSwitch(!monthlyFirstSwitch);
  }

  const getAllDaysInMonth = (month, year) =>
    Array.from(
      { length: new Date(year, month + 1, 0).getDate() },
      (_, i) => i + 1
    );

  // Initial setup useEffect - runs only once on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    setMonthlyDays(getAllDaysInMonth(months.indexOf("Jan"), 2023));
  }, []);

  // Form values sync useEffect - runs when form values change
  useEffect(() => {
    // Parse and initialize r_weekly_day
    const parsedWeeklyDays = parsePHPSerializedArray(form.values.r_weekly_day);
    if (!Array.isArray(form.values.r_weekly_day) || parsedWeeklyDays.length > 0) {
      form.setFieldValue('r_weekly_day', parsedWeeklyDays);
    }

    // Sync selectedDays state with existing form values
    if (parsedWeeklyDays.length > 0) {
      setSelectedDays(parsedWeeklyDays);
    }

    if (form.values.ee_recurrence && form.values.ee_recurrence != '') {
      form.setFieldValue('recurrence', form.values.ee_recurrence);
      setSelectedRepeatPeriod(form.values.ee_recurrence);
    }
  }, [form.values.r_weekly_day, form.values.ee_recurrence]);

  // Auto-select weekly day when start date changes and weekly recurrence is selected
  useEffect(() => {
    if (selectedRepeatPeriod === 'weekly') {
      const startDate = getStartDate();
      if (startDate) {
        const date = new Date(startDate);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        
        // Only auto-select if no days are currently selected
        const currentSelectedDays = form.values.r_weekly_day || [];
        console.log('Auto-selection check:', {
          selectedRepeatPeriod,
          startDate,
          dayName,
          currentSelectedDays,
          selectedDays
        });
        
        if (currentSelectedDays.length === 0) {
          const newSelectedDays = [dayName];
          console.log('Auto-selecting day:', newSelectedDays);
          setSelectedDays(newSelectedDays);
          form.setFieldValue('r_weekly_day', newSelectedDays);
        }
      }
    }
  }, [form.values.ee_start_at, form.values.ae_start_at, form.values.start_at, selectedRepeatPeriod]);



  // Function to parse PHP serialized array
  const parsePHPSerializedArray = (serializedString) => {
    if (!serializedString || typeof serializedString !== 'string') {
      return [];
    }
    
    // Check if it's a PHP serialized array
    if (serializedString.startsWith('a:')) {
      try {
        // Extract the array content using regex
        const match = serializedString.match(/a:\d+:{(.*)}/);
        if (match) {
          const content = match[1];
          const values = [];
          
          // Parse the serialized content to extract string values
          const stringMatches = content.match(/s:\d+:"([^"]+)"/g);
          if (stringMatches) {
            stringMatches.forEach(match => {
              const valueMatch = match.match(/s:\d+:"([^"]+)"/);
              if (valueMatch) {
                values.push(valueMatch[1]);
              }
            });
          }
          

          return values;
        }
      } catch (error) {
        console.error('Error parsing PHP serialized array:', error);
      }
    }
    
    // If it's already an array, return it
    if (Array.isArray(serializedString)) {
      return serializedString;
    }
    
    return [];
  };

  return (
    <Box>
      <FormControl
        mb="12px"
      >
        <FormLabel htmlFor="ee_recurrence" ms="4px" fontSize="sm" fontWeight="normal">
          Event Recurrence
        </FormLabel>
        <Select
        name="ee_recurrence"
        borderRadius="15px"
        fontSize="sm"
        placeholder="Select Recurrence"
        size="lg"
        onChange={onRepeatChange}
        defaultValue={form.values.ee_recurrence}>
        {repeatPeriods?.map((t, index) => (
          <option key={`rp-${index}`} value={t.toLowerCase()}>{t}</option>
        ))
        }
      </Select>
      </FormControl>
      {selectedRepeatPeriod == 'yearly' && (
        <Box mt={2} ml={5} mr={5}>
          <SimpleGrid columns={4} spacing={2}>
            <Switch onChange={onYearlyFirstSwitchChange} isChecked={yearlyFirstSwitch} id="r_yearly_first" defaultValue={form.values.r_yearly_first} mt={3} />
            <Box>
              <Center mt={2}> on</Center>
            </Box>
            <Select
              id="r_yearly_month"
              borderRadius="15px"
              fontSize="sm"
              placeholder="Select Month"
              size="lg"
              disabled={!yearlyFirstSwitch}
              onChange={onYearlyMonthChange}
              defaultValue={form.values.r_yearly_month}>
              {months?.map((t, index) => (
                <option key={`rp-${index}`} value={t}>{t}</option>
              ))}
            </Select>
            <Select
              id="r_yearly_day"
              borderRadius="15px"
              fontSize="sm"
              placeholder="Select Day"
              size="lg"
              disabled={!yearlyFirstSwitch}
              onChange={onYearlyDayChange}
              defaultValue={form.values.r_yearly_day}>
              {yearlyDays?.map((t, index) => (
                <option key={`rp-${index}`} value={t}>{t}</option>
              ))}
            </Select>
          </SimpleGrid>
          <SimpleGrid columns={4} spacing={2} mt={2}>
            <Switch onChange={onYearlySecondSwitchChange} isChecked={yearlySecondSwitch} id="r_yearly_second" defaultValue={form.values.r_yearly_second} mt={3} />
            <Box>
              <Center mt={2}> on the</Center>
            </Box>
            <Box>
              <Select
                id="r_yearly_occurrence"
                borderRadius="15px"
                fontSize="sm"
                placeholder="Select Occurrence"
                size="lg"
                disabled={!yearlySecondSwitch}
                onChange={onYearlyDayChange}
                defaultValue={form.values.r_yearly_occurrence}>
                {occurrencePeriods?.map((t, index) => (
                  <option key={`rop-${index}`} value={t}>{t}</option>
                ))}
              </Select>
              <Select
                id="r_yearly_daytext"
                borderRadius="15px"
                fontSize="sm"
                placeholder="Select Day"
                size="lg"
                mt={2}
                disabled={!yearlySecondSwitch}
                onChange={onYearlyWeekdayChange}
                defaultValue={form.values.r_yearly_daytext}>
                {days?.map((t, index) => (
                  <option key={`rp-${index}`} value={t}>{t}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text mt={2}>of</Text>
              <Select
                id="r_yearly_month"
                borderRadius="15px"
                fontSize="sm"
                placeholder="Select Month"
                size="lg"
                disabled={!yearlySecondSwitch}
                onChange={onYearlySecondMonthChange}
                defaultValue={form.values.r_yearly_second_month}>
                {months?.map((t, index) => (
                  <option key={`rym-${index}`} value={t}>{t}</option>
                ))}
              </Select>
            </Box>
          </SimpleGrid>
        </Box>
      )}
      {selectedRepeatPeriod == 'monthly' && (
        <Box mt={2} ml={5} mr={5}>
          <SimpleGrid columns={3} spacing={2} mt={2}>
            <Box>
              <HStack>
                <Text>every</Text>
                <Input type="number" id="r_monthly_months" onChange={onMonthlyMonthsChange} defaultValue={form.values.r_monthly_months}></Input>
                <Text>months</Text>
              </HStack>
            </Box>
            <Box><Switch onChange={onMonthlyFirstSwitchChange} isChecked={monthlyFirstSwitch} id="r_monthly_first" defaultValue={form.values.r_monthly_first} /> on day</Box>
            <Select
              id="r_monthly_day"
              borderRadius="15px"
              fontSize="sm"
              placeholder="Select Day"
              size="lg"
              disabled={!monthlyFirstSwitch}
              onChange={onMonthlyFirstDayChange}
              defaultValue={form.values.r_monthly_day}>
              {monthlyDays?.map((t, index) => (
                <option key={`rmd-${index}`} value={t}>{t}</option>
              ))}
            </Select>
          </SimpleGrid>
          <SimpleGrid columns={4} spacing={2} mt={2}>
            <Switch onChange={onMonthlySecondSwitchChange} id="r_monthly_second" isChecked={monthlySecondSwitch} mt={3} defaultValue={form.values.r_monthly_second} />
            <Box>
              <Center mt={2}> on the</Center>
            </Box>
            <Box>
              <Select
                id="r_monthly_occurrence"
                borderRadius="15px"
                fontSize="sm"
                placeholder="Select Occurrence"
                size="lg"
                disabled={!monthlySecondSwitch}
                onChange={onMonthlyOccurrenceChange}
                defaultValue={form.values.r_monthly_occurrence}>
                {occurrencePeriods?.map((t, index) => (
                  <option key={`rop-${index}`} value={t}>{t}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Select
                id="r_monthly_daytext"
                borderRadius="15px"
                fontSize="sm"
                placeholder="Select Day"
                size="lg"
                disabled={!monthlySecondSwitch}
                onChange={onMonthlyWeekdayChange}
                defaultValue={form.values.r_monthly_daytext}>
                {days?.map((t, index) => (
                  <option key={`rp-${index}`} value={t}>{t}</option>
                ))}
              </Select>
            </Box>
          </SimpleGrid>
        </Box>
      )}
      {selectedRepeatPeriod == 'weekly' && (
        <Box mt={2} ml={5} mr={5}>
            <Box maxWidth="300px" mt={2}>
              <HStack>
                <Text>every</Text>
                <Input type="number" id="r_weekly_weeks" onChange={onWeeklyWeeksChange} defaultValue={form.values.r_weekly_weeks}></Input>
                <Text>weeks</Text>
              </HStack>
            </Box>
            <Box mt={2}>
              <HStack>
              <Checkbox size='md' colorScheme='green' onChange={onWeeklyDayChange} value="Mon" isChecked={selectedDays.includes("Mon")}>
                Mon
              </Checkbox>
              <Checkbox size='md' colorScheme='green' onChange={onWeeklyDayChange} value="Tue" isChecked={selectedDays.includes("Tue")}>
                Tue
              </Checkbox>
              <Checkbox size='md' colorScheme='green' onChange={onWeeklyDayChange} value="Wed" isChecked={selectedDays.includes("Wed")}>
                Wed
              </Checkbox>
              <Checkbox size='md' colorScheme='green' onChange={onWeeklyDayChange} value="Thu" isChecked={selectedDays.includes("Thu")}>
                Thu
              </Checkbox>
              <Checkbox size='md' colorScheme='green' onChange={onWeeklyDayChange} value="Fri" isChecked={selectedDays.includes("Fri")}>
                Fri
              </Checkbox>
              <Checkbox size='md' colorScheme='green' onChange={onWeeklyDayChange} value="Sat" isChecked={selectedDays.includes("Sat")}>
                Sat
              </Checkbox>
              <Checkbox size='md' colorScheme='green' onChange={onWeeklyDayChange} value="Sun" isChecked={selectedDays.includes("Sun")}>
                Sun
              </Checkbox>
            </HStack>
            </Box>
        </Box>
      )}
      {selectedRepeatPeriod == 'daily' && (
        <Box mt={2} ml={5} mr={5}>
            <Box maxWidth="300px" mt={2}>
              <HStack>
                <Text>every</Text>
                <Input type="number" id="r_daily_days" onChange={onDailyDaysChange} defaultValue={form.values.r_daily_days}></Input>
                <Text>days</Text>
              </HStack>
            </Box>
        </Box>
      )}
      {selectedRepeatPeriod && (
        <Box mt={2} ml={5} mr={5}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            <FormControl mb="12px">
              <FormLabel htmlFor="r_end_type" ms="4px" fontSize="sm" fontWeight="normal">
                End
              </FormLabel>
              <Select
                id="r_end_type"
                borderRadius="15px"
                fontSize="sm"
                placeholder="Select End Type"
                size="lg"
                onChange={onEndTypeChange}
                value={endType}
              >
                <option value="By">By</option>
                <option value="After">After</option>
              </Select>
            </FormControl>

            {endType === 'After' && (
              <FormControl mb="12px">
                <FormLabel htmlFor="r_number_of_occurrences" ms="4px" fontSize="sm" fontWeight="normal">
                  Number of Occurrences
                </FormLabel>
                <Input
                  id="r_number_of_occurrences"
                  type="number"
                  min="1"
                  borderRadius="15px"
                  fontSize="sm"
                  placeholder="Enter number of occurrences"
                  size="lg"
                  onChange={onNumberOfOccurrencesChange}
                  value={numberOfOccurrences}
                />
              </FormControl>
            )}

            <FormControl mb="12px">
              <FormLabel htmlFor="r_end_date" ms="4px" fontSize="sm" fontWeight="normal">
                Recurrence End Date
              </FormLabel>
              <Input
                {...field}
                id="r_end_date"
                borderRadius="15px"
                fontSize="sm"
                placeholder="Select Date and Time"
                size="lg"
                type="datetime-local"
                onChange={onRecurrenceEndChange}
                value={form.values.r_end_date}
              />
            </FormControl>

          </SimpleGrid>

          {/* Recurrence Summary */}
          <Box mt={4} mb={4} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" textAlign="left">
            <Text fontWeight="bold" mb={2} color="gray.700">
              Recurrence Summary
            </Text>
            <Text fontSize="sm" color="gray.600" lineHeight="1.6">
              {generateRecurrenceSummary()}
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Recurrence;
